import { ReactNode } from "react";
import { DataNode } from "domhandler";
import parse, { DOMNode, Element, domToReact } from "html-react-parser";
import Link from "next/link";

import { slugify } from "@/utils/slugify";

export type TocItem = {
        id: string;
        title: string;
        level: 2 | 3;
};

export function getNodeText(node: DOMNode): string {
        if ((node as DataNode).type === "text") {
                return (node as DataNode).data ?? "";
        }

        if (node instanceof Element) {
                return (node.children as DOMNode[]).map(getNodeText).join("");
        }

        return "";
}

function createHeadingId(
        domNode: Element,
        fallbackIndex: number,
        existingIds: Map<string, number>
): string {
        const existingId = domNode.attribs?.id?.trim();
        if (existingId) return existingId;

        const text = getNodeText(domNode).trim();
        const baseSlug = slugify(text) || `${domNode.name}-${fallbackIndex}`;
        const currentCount = existingIds.get(baseSlug) ?? 0;
        existingIds.set(baseSlug, currentCount + 1);

        if (currentCount === 0) return baseSlug;

        return `${baseSlug}-${currentCount + 1}`;
}

type ParseContentOptions = {
        emptyState?: ReactNode;
        headingClassName?: string;
};

export function parseContentWithToc(
        content: string,
        { emptyState, headingClassName }: ParseContentOptions = {}
): { parsedContent: ReactNode; tocItems: TocItem[] } {
        if (typeof content !== "string" || !content.trim()) {
                return {
                        parsedContent: emptyState ?? null,
                        tocItems: [],
                };
        }

        const collectedHeadings: TocItem[] = [];
        const slugRegistry = new Map<string, number>();

        const parsedContent = parse(content, {
                replace: (domNode: DOMNode) => {
                        if (domNode instanceof Element) {
                                if (domNode.name === "a") {
                                        const href = domNode.attribs?.href;
                                        if (!href) return;

                                        const isInternal =
                                                href.startsWith("/") || href.startsWith("#");
                                        const children = domToReact(domNode.children as DOMNode[]);

                                        if (isInternal) {
                                                return (
                                                        <Link
                                                                href={href}
                                                                className="text-primary font-semibold hover:underline"
                                                        >
                                                                {children}
                                                        </Link>
                                                );
                                        }

                                        return (
                                                <a
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary font-semibold hover:underline"
                                                >
                                                        {children}
                                                </a>
                                        );
                                }

                                if (domNode.name === "h2") {
                                        const id = createHeadingId(
                                                domNode,
                                                collectedHeadings.length + 1,
                                                slugRegistry
                                        );
                                        const level = 2;
                                        const titleText = getNodeText(domNode).trim();

                                        if (titleText) {
                                                collectedHeadings.push({
                                                        id,
                                                        title: titleText,
                                                        level,
                                                });
                                        }

                                        const existingClass = domNode.attribs?.class ?? "";
                                        const className = [existingClass, headingClassName]
                                                .filter(Boolean)
                                                .join(" ");

                                        const HeadingTag = domNode.name as "h2" | "h3";

                                        return (
                                                <HeadingTag id={id} className={className}>
                                                        {domToReact(domNode.children as DOMNode[])}
                                                </HeadingTag>
                                        );
                                }
                        }
                },
        });

        return { parsedContent, tocItems: collectedHeadings };
}
